/** Canonical record-id extraction. Record rows carry their primary key as `ID` (legacy/Zoho) or `id` (D1); callers used
 *  to re-derive this with divergent coercion (`X.ID ?? X.id`, `String(... ?? '')`, bare casts), which mints the literal
 *  string "undefined" as a Map key and lets an id-less row match another id-less row. This resolver is the one shape:
 *  first usable key wins, coerced to a trimmed string, and a record with no usable id is null -- never "undefined". */

import { toSafeString } from '@federa/const/string-guards';
const RECORD_ID_KEYS = ['ID', 'id'] as const;

/** Resolve a record's primary id to a trimmed non-empty string, or null when no key holds a usable value. `keys` covers
 *  the record shapes with a different alias vocabulary (property-feed items read `ID` then `propertyId`). */
export function resolveRecordId(
	record: Record<string, unknown> | null | undefined,
	keys: readonly string[] = RECORD_ID_KEYS,
): string | null {
	if (!record) return null;
	for (const key of keys) {
		const raw = record[key];
		if (raw == null) continue;
		const value = toSafeString(raw);
		if (value) return value;
	}
	return null;
}
